/**
 * YourSupportJourneyCard Component
 * Unified card showing progress + next recommended action
 * Replaces both NextStepsWidget and SupportJourney from old dashboard
 *
 * When the user has not indicated hjelpestønad interest and has no draft,
 * shows a neutral discovery state instead of the journey tracker.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../common/Card';
import Button from '../common/Button';
import { FaCheckCircle, FaClock, FaArrowRight, FaCompass } from 'react-icons/fa';

const YourSupportJourneyCard = ({
  journeyStage,
  progress,
  nextAction,
  onActionClick,
  hasHjelpestonadInterest,
  latestDraft
}) => {
  const navigate = useNavigate();

  // If no hjelpestønad interest and no draft, show discovery state
  if (!hasHjelpestonadInterest) {
    return (
      <Card variant="white" padding="lg" className="mb-6">
        <div className="space-y-4">
          <div>
            <h2 className="font-display text-xl font-semibold text-text-primary mb-1">
              Din støttereise
            </h2>
            <p className="text-sm text-text-secondary">
              Finn ut hvilke stønader som kan passe din situasjon
            </p>
          </div>

          <div className="bg-sage-50 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-sage-100 text-sage-600 flex items-center justify-center">
                <FaCompass className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-sage-900 mb-1">
                  Utforsk hvilke stønader du kan ha krav på
                </p>
                <p className="text-sm text-sage-700 mb-4">
                  Start en samtale med oss, så hjelper vi deg å kartlegge mulighetene dine.
                </p>
                <Button
                  variant="secondary"
                  size="md"
                  onClick={() => navigate('/new-conversation')}
                  iconRight={<FaArrowRight className="w-3.5 h-3.5" />}
                >
                  Start en samtale
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  // Derive step-level progress from draft status
  let derivedProgress = progress;
  if (latestDraft) {
    const statusMap = {
      in_progress: 2,
      summary_reviewed: 2,
      generated: 3,
      downloaded: 4
    };
    const draftProgress = statusMap[latestDraft.status] || 0;
    if (draftProgress > derivedProgress) {
      derivedProgress = draftProgress;
    }
  }

  // Derive CTA from draft status when nextAction doesn't already cover it
  let journeyAction = nextAction;
  if (!journeyAction && latestDraft) {
    switch (latestDraft.status) {
      case 'in_progress':
        journeyAction = {
          label: 'Fortsett søknad',
          description: 'Du har en påstartet søknad. Fortsett der du slapp.',
          action: () => navigate(`/soknad/hjelpestonad/${latestDraft._id}`)
        };
        break;
      case 'summary_reviewed':
      case 'generated':
        journeyAction = {
          label: 'Se gjennom søknaden',
          description: 'Søknaden din er klar. Se over og last ned for å sende til Nav.',
          action: () => navigate(`/soknad/hjelpestonad/${latestDraft._id}/preview`)
        };
        break;
      case 'downloaded':
        journeyAction = {
          label: 'Last ned igjen',
          description: 'Du har allerede lastet ned søknaden. Har du sendt den til Nav?',
          action: () => navigate(`/soknad/hjelpestonad/${latestDraft._id}/preview`)
        };
        break;
      default:
        break;
    }
  }

  // If still no action and no draft, show start button
  if (!journeyAction && !latestDraft) {
    journeyAction = {
      label: 'Start hjelpestønad-søknad',
      description: 'Begynn på søknaden om hjelpestønad',
      action: () => navigate('/soknad/hjelpestonad')
    };
  }

  // Journey stages configuration
  const stages = [
    {
      id: 'situation_mapped',
      label: 'Kartlagt situasjon',
      icon: FaCheckCircle,
      completed: derivedProgress >= 1
    },
    {
      id: 'started_application',
      label: 'Startet søknad',
      icon: derivedProgress >= 2 ? FaCheckCircle : FaClock,
      completed: derivedProgress >= 2
    },
    {
      id: 'application_ready',
      label: 'Søknad klar',
      icon: derivedProgress >= 3 ? FaCheckCircle : FaClock,
      completed: derivedProgress >= 3
    },
    {
      id: 'submitted',
      label: 'Sendt til Nav',
      icon: derivedProgress >= 4 ? FaCheckCircle : FaClock,
      completed: derivedProgress >= 4
    }
  ];

  return (
    <Card variant="white" padding="lg" className="mb-6">
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h2 className="font-display text-xl font-semibold text-text-primary mb-1">
            Din støttereise
          </h2>
          <p className="text-sm text-text-secondary">
            Du har fullført {derivedProgress} av {stages.length} steg
          </p>
        </div>

        {/* Progress visualization */}
        <div className="relative">
          {/* Progress bar background */}
          <div className="absolute top-5 left-0 w-full h-1 bg-sage-100 rounded-full" />

          {/* Progress bar fill - sage for growth/progress */}
          <div
            className="absolute top-5 left-0 h-1 bg-sage rounded-full transition-all duration-500 ease-out-soft"
            style={{ width: `${(derivedProgress / stages.length) * 100}%` }}
          />

          {/* Stage indicators */}
          <div className="relative flex justify-between">
            {stages.map((stage, index) => {
              const Icon = stage.icon;
              const isCompleted = stage.completed;
              const isCurrent = index === derivedProgress && !isCompleted;

              return (
                <div key={stage.id} className="flex flex-col items-center">
                  {/* Icon */}
                  <div
                    className={`
                      w-10 h-10 rounded-full flex items-center justify-center
                      transition-all duration-300 ease-smooth
                      ${isCompleted
                        ? 'bg-sage-100 text-sage-600'
                        : isCurrent
                        ? 'bg-warning-100 text-warning-600'
                        : 'bg-neutral-100 text-neutral-400'
                      }
                    `}
                  >
                    <Icon className="w-5 h-5" />
                  </div>

                  {/* Label */}
                  <p
                    className={`
                      mt-2 text-xs text-center max-w-[80px]
                      ${isCompleted || isCurrent ? 'text-text-primary font-medium' : 'text-text-muted'}
                    `}
                  >
                    {stage.label}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Next action section */}
        {journeyAction && (
          <div className="pt-4 border-t border-border-light">
            <div className="bg-sage-50 rounded-lg p-4">
              <p className="text-sm font-semibold text-sage-900 mb-1">
                Anbefalt neste steg
              </p>
              <p className="text-sm text-sage-700 mb-4">
                {journeyAction.description}
              </p>
              <Button
                variant="secondary"
                size="md"
                onClick={() => onActionClick(journeyAction)}
                iconRight={<FaArrowRight className="w-3.5 h-3.5" />}
              >
                {journeyAction.label}
              </Button>
            </div>
          </div>
        )}

        {/* Completed state */}
        {!journeyAction && derivedProgress === stages.length && (
          <div className="pt-4 border-t border-border-light">
            <div className="bg-sage-50 rounded-lg p-4">
              <p className="text-sm font-medium text-sage-900 mb-1">
                Flott jobbet!
              </p>
              <p className="text-sm text-sage-700">
                Du har fullført hjelpestønad-søknaden. Vi kan nå hjelpe deg med andre ytelser som grunnstønad eller avlastning.
              </p>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

export default YourSupportJourneyCard;
